import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
  interface ImportMeta {
    env: Record<string, any>;
  }
}

let isInitialized = false;

/**
 * Initializes Google Analytics 4 in production environments.
 * Disables automatic page_view tracking to prevent duplicate tracking
 * when managed by React Router navigation listeners.
 */
export const initGA = () => {
  if (isInitialized || !import.meta.env.PROD) return;

  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-1E7JWTPGSF';
  if (!measurementId) return;

  // Prevent duplicate script injections
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
    isInitialized = true;
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
  });

  const injectScript = () => {
    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) return;
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  };

  if (document.readyState === 'complete') {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(injectScript);
    } else {
      setTimeout(injectScript, 500);
    }
  } else {
    window.addEventListener('load', () => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(injectScript);
      } else {
        setTimeout(injectScript, 500);
      }
    });
  }

  isInitialized = true;
};

/**
 * Logs a standard GA4 page_view event.
 */
export const logPageView = (path: string) => {
  if (!import.meta.env.PROD || !window.gtag) return;
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-1E7JWTPGSF';
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    send_to: measurementId,
  });
};

/**
 * Logs a custom GA4 event with optional parameters.
 */
export const logEvent = (eventName: string, params?: Record<string, any>) => {
  if (!import.meta.env.PROD || !window.gtag) return;
  window.gtag('event', eventName, params);
};

/**
 * Evaluates route pathname and logs category visits.
 */
export const trackRouteVisit = (pathname: string) => {
  if (!import.meta.env.PROD) return;

  logPageView(pathname);

  if (pathname.startsWith('/calculators') || pathname.startsWith('/calculator')) {
    logEvent('calculator_visit', { path: pathname });
  } else if (pathname.startsWith('/guides')) {
    logEvent('guide_visit', { path: pathname });
  } else if (pathname.startsWith('/compare')) {
    logEvent('compare_visit', { path: pathname });
  } else if (pathname.startsWith('/ifsc')) {
    logEvent('ifsc_visit', { path: pathname });
  }
};

/**
 * Custom React hook to automate GA4 page_view and section visit tracking
 * across React Router navigation changes.
 */
export function useAnalytics() {
  const location = useLocation();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    trackRouteVisit(location.pathname + location.search);
  }, [location.pathname, location.search]);
}
